import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Switch,
  Divider,
  Tooltip,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardTour } from '../hooks/useDashboardTour';
import { formatDate } from '../i18n/format';
import { api } from '../services/api';
import { auditsApi } from '../services/audits';
import { tasksApi } from '../services/tasks';
import { competitorsApi } from '../services/competitors';
import { BrandFavicon } from '../components/brand/BrandFavicon';
import DomainFavicon from '../components/competitors/DomainFavicon';
import { EngineLogo, ENGINE_LABEL, ENGINE_COLOR } from './dashboard/EngineLogo';
import { Flag, ZoneFlag, sizeLabel } from './dashboard/Flag';
import { resolveZone, languageFlag } from './dashboard/geoFlags';
import { useDashboardLayout, type DashboardCardId } from './dashboard/useDashboardLayout';
import {
  type DateRange,
  GEO_WEIGHTS,
  computeBrandRollup,
  computeGeoScore,
  filterByRange,
  latestScore,
  latestEngineCitationCounts,
  mergeEngineCitationCounts,
  latestLanguageScores,
  latestCitedPages,
  competitorRanking,
  type CompetitorRankRow,
} from './dashboard/dashboardData';
import type {
  Audit,
  Task,
  AxisMatrixResponse,
  CitedPageEntry,
  LanguageScoreEntry,
  ZoneVisibilityBreakdown,
} from '@traaker/shared';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip, Legend);

const COLORS = {
  geo: '#7C3AED',
  narrative: '#7C3AED',
  authority: '#0E9F6E',
  referral: '#EA580C',
  earnedMedia: '#0EA5E9',
  up: '#10B981',
  down: '#EF4444',
};

// Language code → i18n key (resolved with t() at render).
const LANG_LABEL_KEY: Record<string, string> = {
  fr: 'dashboard.lang.fr',
  en: 'dashboard.lang.en',
  es: 'dashboard.lang.es',
  de: 'dashboard.lang.de',
  it: 'dashboard.lang.it',
  pt: 'dashboard.lang.pt',
  nl: 'dashboard.lang.nl',
};

/** Accent/case-insensitive fold for matching matrix names → tracked domains. */
function foldCompetitorName(s: string): string {
  return (s || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function scoreBarColor(v: number): string {
  if (v < 35) return '#EF4444';
  if (v < 60) return '#F59E0B';
  return '#10B981';
}

// Date-range → i18n key (resolved with t() at render).
const RANGE_LABEL_KEY: Record<DateRange, string> = {
  '7d': 'dashboard.range.7d',
  '30d': 'dashboard.range.30d',
  '90d': 'dashboard.range.90d',
  '180d': 'dashboard.range.180d',
  all: 'dashboard.range.all',
};

// Card definitions carry i18n keys for label + sub (resolved with t() at render).
const CARD_DEFS: { id: DashboardCardId; labelKey: string; subKey: string }[] = [
  { id: 'geo', labelKey: 'dashboard.card.geo.label', subKey: 'dashboard.card.geo.sub' },
  { id: 'tasks', labelKey: 'dashboard.card.tasks.label', subKey: 'dashboard.card.tasks.sub' },
  { id: 'competitors', labelKey: 'dashboard.card.competitors.label', subKey: 'dashboard.card.competitors.sub' },
  { id: 'engines', labelKey: 'dashboard.card.engines.label', subKey: 'dashboard.card.engines.sub' },
  { id: 'pages', labelKey: 'dashboard.card.pages.label', subKey: 'dashboard.card.pages.sub' },
  { id: 'zones', labelKey: 'dashboard.card.zones.label', subKey: 'dashboard.card.zones.sub' },
  { id: 'languages', labelKey: 'dashboard.card.languages.label', subKey: 'dashboard.card.languages.sub' },
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { activeBrand } = useBrand();
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { maybeAutoStart, start: startTour } = useDashboardTour();
  const layout = useDashboardLayout();
  const [range, setRange] = useState<DateRange>('30d');
  const [loading, setLoading] = useState(true);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [websiteAudits, setWebsiteAudits] = useState<Audit[]>([]);
  const [narrativeAudits, setNarrativeAudits] = useState<Audit[]>([]);
  const [authorityAudits, setAuthorityAudits] = useState<Audit[]>([]);
  const [referralAudits, setReferralAudits] = useState<Audit[]>([]);
  const [earnedAudits, setEarnedAudits] = useState<Audit[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [referralMatrix, setReferralMatrix] = useState<AxisMatrixResponse | null>(null);
  // Tracked-competitor name → domain, for the Concurrents card favicons (the
  // matrix footer only carries names; favicons are keyed by domain like the
  // Competitors page). Folded (accent/case-insensitive) keys.
  const [competitorDomains, setCompetitorDomains] = useState<Record<string, string>>({});

  const loadData = useCallback(async () => {
    if (!activeBrand) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Each call is independently caught: a single failing surface (e.g. tasks
      // returns 403 when the feature is off for basic trials) must NOT blank the
      // whole dashboard. A bare Promise.all would reject on the first rejection,
      // leaving every audit array empty → false "aucun audit terminé".
      const empty: Audit[] = [];
      const [website, narrative, authority, referral, earned, allTasks, matrix, competitors] = await Promise.all([
        auditsApi.list('website', activeBrand.brandId).catch(() => empty),
        auditsApi.list('brand_narrative', activeBrand.brandId).catch(() => empty),
        auditsApi.list('brand_authority', activeBrand.brandId).catch(() => empty),
        auditsApi.list('brand_referral', activeBrand.brandId).catch(() => empty),
        auditsApi.list('earned_media', activeBrand.brandId).catch(() => empty),
        tasksApi.list(activeBrand.brandId).catch(() => [] as Task[]),
        auditsApi.getAxisMatrix(activeBrand.brandId, 'brand_referral', 1).catch(() => null),
        competitorsApi.list(activeBrand.brandId).catch(() => []),
      ]);
      const doneSorted = (arr: Audit[]) =>
        arr.filter((a) => a.status === 'completed').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      setWebsiteAudits(doneSorted(website));
      setNarrativeAudits(doneSorted(narrative));
      setAuthorityAudits(doneSorted(authority));
      setReferralAudits(doneSorted(referral));
      setEarnedAudits(doneSorted(earned));
      setTasks(allTasks.sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)));
      setReferralMatrix(matrix);
      const domMap: Record<string, string> = {};
      for (const c of competitors) {
        if (c.name && c.domain) domMap[foldCompetitorName(c.name)] = c.domain;
      }
      setCompetitorDomains(domMap);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Post-onboarding product tour — OPT-IN (2026-07-31): a fresh onboard gets a
  // small invitation dialog instead of the 13-step tour auto-starting on top of
  // their first-ever results (signup-audit finding: one more forced layer on an
  // already long funnel). Accept → the existing auto-start (stamps seen on
  // completion); decline → stamp seen directly so it never asks again (the
  // Settings "Revoir la visite" replay stays available). Replay path unchanged.
  const navState = location.state as { justOnboarded?: boolean; replayTour?: boolean } | null;
  const justOnboarded = navState?.justOnboarded;
  const replayTour = navState?.replayTour;
  const tourFiredRef = useRef(false);
  const [tourInviteOpen, setTourInviteOpen] = useState(false);
  useEffect(() => {
    if (tourFiredRef.current) return;
    if (loading || !activeBrand) return;
    if (!justOnboarded && !replayTour) return;
    window.history.replaceState({}, '');
    if (!replayTour && user?.tourState?.dashboardTourSeenAt) return;
    tourFiredRef.current = true;
    if (replayTour) {
      const id = window.setTimeout(() => startTour(), 150);
      return () => window.clearTimeout(id);
    }
    setTourInviteOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, justOnboarded, replayTour, activeBrand, user]);

  const acceptTourInvite = () => {
    setTourInviteOpen(false);
    window.setTimeout(() => maybeAutoStart(), 150);
  };
  const declineTourInvite = () => {
    setTourInviteOpen(false);
    // Same fire-and-forget stamp the tour uses on completion — never ask again.
    api.patch('/users/me/tour-seen', {}).then(() => refreshProfile()).catch(() => {});
  };

  const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const openTasks = useMemo(
    () =>
      tasks
        .filter((t) => t.status === 'todo' || t.status === 'in_progress')
        .sort((a, b) => {
          const pa = PRIORITY_ORDER[a.priority] ?? 9;
          const pb = PRIORITY_ORDER[b.priority] ?? 9;
          if (pa !== pb) return pa - pb;
          return b.updatedAt.localeCompare(a.updatedAt);
        }),
    [tasks],
  );

  const filtered = useMemo(
    () => ({
      website: filterByRange(websiteAudits, range),
      narrative: filterByRange(narrativeAudits, range),
      authority: filterByRange(authorityAudits, range),
      referral: filterByRange(referralAudits, range),
      earned: filterByRange(earnedAudits, range),
    }),
    [websiteAudits, narrativeAudits, authorityAudits, referralAudits, earnedAudits, range],
  );

  const totalAudits =
    filtered.website.length +
    filtered.narrative.length +
    filtered.authority.length +
    filtered.referral.length +
    filtered.earned.length;

  const hasHistory = useMemo(() => {
    const maxLen = Math.max(
      narrativeAudits.length,
      authorityAudits.length,
      referralAudits.length,
      websiteAudits.length,
      earnedAudits.length,
    );
    return maxLen >= 2;
  }, [narrativeAudits, authorityAudits, referralAudits, websiteAudits, earnedAudits]);

  const lastAuditLabel = useMemo(() => {
    const all = [...websiteAudits, ...narrativeAudits, ...authorityAudits, ...referralAudits, ...earnedAudits]
      .map((a) => a.createdAt)
      .sort();
    const last = all.length ? all[all.length - 1] : null;
    if (!last) return null;
    const diffDays = Math.floor((Date.now() - new Date(last).getTime()) / 86400000);
    if (diffDays === 0) return t('dashboard.lastAudit.today');
    if (diffDays === 1) return t('dashboard.lastAudit.yesterday');
    if (diffDays < 30) return t('dashboard.lastAudit.daysAgo', { count: diffDays });
    if (diffDays < 60) return t('dashboard.lastAudit.oneMonthAgo');
    return t('dashboard.lastAudit.monthsAgo', { count: Math.floor(diffDays / 30) });
  }, [websiteAudits, narrativeAudits, authorityAudits, referralAudits, earnedAudits, t]);

  const isFirstAudit = totalAudits > 0 && !hasHistory;

  return (
    <Box>
      <Dialog open={tourInviteOpen} onClose={declineTourInvite} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>{t('dashboard.tourInvite.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dashboard.tourInvite.body')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={declineTourInvite}>{t('dashboard.tourInvite.decline')}</Button>
          <Button variant="contained" onClick={acceptTourInvite}>{t('dashboard.tourInvite.accept')}</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box data-tour="dashboard-header">
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            {t('dashboard.title')}
          </Typography>
          {activeBrand && (
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.performanceOf')}{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {activeBrand.name}
              </Box>
              {isFirstAudit ? (
                <> · {t('dashboard.firstAuditCompleted', { label: lastAuditLabel })}</>
              ) : (
                lastAuditLabel && <> · {t('dashboard.lastAuditLine', { label: lastAuditLabel })}</>
              )}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup value={range} exclusive onChange={(_, v) => v && setRange(v)} size="small">
            <ToggleButton value="7d">{t('dashboard.rangeToggle.7d')}</ToggleButton>
            <ToggleButton value="30d" disabled={!hasHistory}>{t('dashboard.rangeToggle.30d')}</ToggleButton>
            <ToggleButton value="90d" disabled={!hasHistory}>{t('dashboard.rangeToggle.90d')}</ToggleButton>
            <ToggleButton value="180d" disabled={!hasHistory}>{t('dashboard.rangeToggle.180d')}</ToggleButton>
            <ToggleButton value="all" disabled={!hasHistory}>{t('dashboard.rangeToggle.all')}</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={() => setCustomizeOpen(true)}>
            {t('dashboard.customize')}
          </Button>
        </Box>
      </Box>

      {!activeBrand ? (
        <Alert severity="info">{t('dashboard.selectBrand')}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : totalAudits === 0 ? (
        <Alert severity="info" data-tour="kpi-row">
          {t('dashboard.noAudits', { name: activeBrand.name })}
        </Alert>
      ) : (
        <Grid container spacing={2.5} data-tour="kpi-row">
          {layout.isVisible('geo') && (
            <Grid size={{ xs: 12, md: 8 }} data-tour="dashboard-tabs">
              <GeoEvolutionCard
                website={filtered.website}
                narrative={filtered.narrative}
                authority={filtered.authority}
                referral={filtered.referral}
                earned={filtered.earned}
                range={range}
                brandCreatedAt={activeBrand.createdAt}
              />
            </Grid>
          )}
          {layout.isVisible('tasks') && (
            <Grid size={{ xs: 12, md: 4 }}>
              <TasksCard tasks={openTasks} isFirstAudit={isFirstAudit} />
            </Grid>
          )}
          {layout.isVisible('competitors') && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <CompetitorsCard
                matrix={referralMatrix}
                brandName={activeBrand.name}
                brandId={activeBrand.brandId}
                competitorDomains={competitorDomains}
                isFirstAudit={isFirstAudit}
                onCta={() => navigate('/competitors')}
              />
            </Grid>
          )}
          {layout.isVisible('engines') && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <EnginesCard
                narrative={filtered.narrative}
                authority={filtered.authority}
                referral={filtered.referral}
                isFirstAudit={isFirstAudit}
              />
            </Grid>
          )}
          {layout.isVisible('pages') && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <CitedPagesCard
                narrative={filtered.narrative}
                authority={filtered.authority}
                referral={filtered.referral}
              />
            </Grid>
          )}
          {layout.isVisible('zones') && (
            <Grid size={{ xs: 12, md: 6 }}>
              <ZonesCard referral={filtered.referral} locations={activeBrand.locations} isFirstAudit={isFirstAudit} />
            </Grid>
          )}
          {layout.isVisible('languages') && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LanguagesCard
                narrative={filtered.narrative}
                authority={filtered.authority}
                referral={filtered.referral}
                isFirstAudit={isFirstAudit}
              />
            </Grid>
          )}
          {CARD_DEFS.every((c) => !layout.isVisible(c.id)) && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                {t('dashboard.allCardsHidden')}
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      <CustomizeDrawer open={customizeOpen} onClose={() => setCustomizeOpen(false)} layout={layout} />
    </Box>
  );
}

/* ──────────────── Card shell ──────────────── */

function CardShell({
  title,
  dotColor,
  link,
  action,
  children,
}: {
  title: string;
  dotColor?: string;
  link?: { label: string; to: string };
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2.5, flex: 1, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.6875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {dotColor && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />}
            {title}
          </Typography>
          {action}
          {link && (
            <Box
              component={RouterLink}
              to={link.to}
              sx={{ fontSize: 12.5, color: 'primary.main', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
            >
              {link.label} →
            </Box>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 2, textAlign: 'center' }}>
      {children}
    </Typography>
  );
}

/* ──────────────── Score GEO — évolution ──────────────── */

function GeoEvolutionCard({
  website,
  narrative,
  authority,
  referral,
  earned,
  range,
  brandCreatedAt,
}: {
  website: Audit[];
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
  earned: Audit[];
  range: DateRange;
  brandCreatedAt?: string;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<'score' | 'cit'>('score');

  const latestWs = latestScore(website, (a) => a.websiteSummary?.overallGeoScore);
  const latestNar = latestScore(narrative, (a) => a.brandNarrativeSummary?.score);
  const latestAuth = latestScore(authority, (a) => a.brandAuthoritySummary?.score);
  const latestRef = latestScore(referral, (a) => a.brandReferralSummary?.score);
  const latestEm = latestScore(earned, (a) => a.earnedMediaSummary?.overall_earned_media_score);
  const latestGeo = computeGeoScore(latestWs, computeBrandRollup(latestNar, latestAuth, latestRef), latestEm);

  const geoSeries = useMemo(() => {
    type Ev = { t: number; kind: 'w' | 'n' | 'a' | 'r' | 'e'; v: number | null };
    const evs: Ev[] = [
      ...website.map<Ev>((a) => ({ t: new Date(a.createdAt).getTime(), kind: 'w', v: a.websiteSummary?.overallGeoScore ?? null })),
      ...narrative.map<Ev>((a) => ({ t: new Date(a.createdAt).getTime(), kind: 'n', v: a.brandNarrativeSummary?.score ?? null })),
      ...authority.map<Ev>((a) => ({ t: new Date(a.createdAt).getTime(), kind: 'a', v: a.brandAuthoritySummary?.score ?? null })),
      ...referral.map<Ev>((a) => ({ t: new Date(a.createdAt).getTime(), kind: 'r', v: a.brandReferralSummary?.score ?? null })),
      ...earned.map<Ev>((a) => ({ t: new Date(a.createdAt).getTime(), kind: 'e', v: a.earnedMediaSummary?.overall_earned_media_score ?? null })),
    ].sort((x, y) => x.t - y.t);
    let lw: number | null = null, ln: number | null = null, la: number | null = null, lr: number | null = null, le: number | null = null;
    const pts: { t: number; value: number | null }[] = [];
    for (const e of evs) {
      if (e.v !== null) {
        if (e.kind === 'w') lw = e.v;
        if (e.kind === 'n') ln = e.v;
        if (e.kind === 'a') la = e.v;
        if (e.kind === 'r') lr = e.v;
        if (e.kind === 'e') le = e.v;
      }
      const g = computeGeoScore(lw, computeBrandRollup(ln, la, lr), le);
      if (g !== null) pts.push({ t: e.t, value: Math.round(g) });
    }
    if (pts.length === 1 && brandCreatedAt) {
      const baseT = new Date(brandCreatedAt).getTime();
      if (Number.isFinite(baseT) && baseT < pts[0].t) return [{ t: baseT, value: 0 }, ...pts];
    }
    return pts;
  }, [website, narrative, authority, referral, earned, brandCreatedAt]);

  const citSeries = useMemo(() => {
    const byDate = new Map<number, Record<string, number>>();
    const add = (iso: string, counts?: Record<string, number>) => {
      if (!counts) return;
      const t = new Date(iso).getTime();
      const cur = byDate.get(t) || {};
      for (const [e, n] of Object.entries(counts)) cur[e] = (cur[e] || 0) + n;
      byDate.set(t, cur);
    };
    narrative.forEach((a) => add(a.createdAt, a.brandNarrativeSummary?.engine_citation_counts));
    authority.forEach((a) => add(a.createdAt, a.brandAuthoritySummary?.engine_citation_counts));
    referral.forEach((a) => add(a.createdAt, a.brandReferralSummary?.engine_citation_counts));
    const pts = [...byDate.entries()].map(([t, counts]) => ({ t, counts })).sort((x, y) => x.t - y.t);
    if (pts.length === 1 && brandCreatedAt) {
      const baseT = new Date(brandCreatedAt).getTime();
      if (Number.isFinite(baseT) && baseT < pts[0].t) {
        const zero: Record<string, number> = {};
        for (const e of Object.keys(pts[0].counts)) zero[e] = 0;
        return [{ t: baseT, counts: zero }, ...pts];
      }
    }
    return pts;
  }, [narrative, authority, referral, brandCreatedAt]);

  const engines = useMemo(() => {
    const set = new Set<string>();
    citSeries.forEach((p) => Object.keys(p.counts).forEach((e) => set.add(e)));
    return [...set];
  }, [citSeries]);

  const fmt = (t: number) => formatDate(t, { day: '2-digit', month: 'short' });

  const scoreData = {
    labels: geoSeries.map((p) => fmt(p.t)),
    datasets: [
      {
        label: t('dashboard.geo.scoreLabel'),
        data: geoSeries.map((p) => p.value),
        borderColor: COLORS.geo,
        backgroundColor: 'rgba(124,58,237,0.12)',
        fill: true,
        tension: 0.35,
        spanGaps: true,
        borderWidth: 2.5,
        pointRadius: geoSeries.length <= 2 ? 4 : 0,
        pointHoverRadius: 5,
        pointBackgroundColor: COLORS.geo,
      },
    ],
  };

  const citData = {
    labels: citSeries.map((p) => fmt(p.t)),
    datasets: engines.map((engine) => ({
      label: ENGINE_LABEL[engine] || engine,
      data: citSeries.map((p) => p.counts[engine] ?? 0),
      borderColor: ENGINE_COLOR[engine] || '#888',
      backgroundColor: 'transparent',
      tension: 0.3,
      spanGaps: true,
      borderWidth: 2.5,
      pointRadius: citSeries.length <= 2 ? 4 : 0,
      pointHoverRadius: 5,
      pointBackgroundColor: ENGINE_COLOR[engine] || '#888',
    })),
  };

  const scoreOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      y: { min: 0, max: 100, grid: { color: 'rgba(11,11,15,0.06)' }, ticks: { maxTicksLimit: 4 } },
      x: { grid: { display: false }, ticks: { maxTicksLimit: 8, maxRotation: 0 } },
    },
  } as const;
  const citOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      y: { min: 0, grid: { color: 'rgba(11,11,15,0.06)' }, ticks: { maxTicksLimit: 4, precision: 0 } },
      x: { grid: { display: false }, ticks: { maxTicksLimit: 8, maxRotation: 0 } },
    },
  } as const;

  const latestCit = mergeEngineCitationCounts(
    latestEngineCitationCounts(narrative, (a) => a.brandNarrativeSummary),
    latestEngineCitationCounts(authority, (a) => a.brandAuthoritySummary),
    latestEngineCitationCounts(referral, (a) => a.brandReferralSummary),
  );

  return (
    <CardShell
      title={t('dashboard.geo.evolution')}
      dotColor={COLORS.geo}
      action={
        <ToggleButtonGroup value={view} exclusive size="small" onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="score" sx={{ py: 0.25, px: 1, fontSize: 11 }}>{t('dashboard.geo.scoreLabel')}</ToggleButton>
          <ToggleButton value="cit" sx={{ py: 0.25, px: 1, fontSize: 11 }}>{t('dashboard.geo.citationsPerEngine')}</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <ScoreRing value={latestGeo} />
        <Stack spacing={1} sx={{ flex: 1, minWidth: 200 }}>
          <AxisBar label={t('dashboard.axis.narrative')} value={latestNar} color={COLORS.narrative} />
          <AxisBar label={t('dashboard.axis.authority')} value={latestAuth} color={COLORS.authority} />
          <AxisBar label={t('dashboard.axis.referral')} value={latestRef} color={COLORS.referral} />
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
            {t('dashboard.geo.weights', {
              brand: Math.round(GEO_WEIGHTS.brandPerception * 100),
              website: Math.round(GEO_WEIGHTS.website * 100),
              earned: Math.round(GEO_WEIGHTS.earnedMedia * 100),
            })}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ height: 180 }}>
        {view === 'score' ? <Line data={scoreData} options={scoreOptions} /> : <Line data={citData} options={citOptions} />}
      </Box>

      {view === 'cit' && engines.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {engines.map((e) => (
            <Box key={e} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>
              <EngineLogo engine={e} size={16} />
              {ENGINE_LABEL[e] || e} <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{latestCit[e] ?? 0}</Box>
            </Box>
          ))}
        </Box>
      )}
      {view === 'cit' && engines.length === 0 && (
        <EmptyHint>{t('dashboard.geo.citationsEmpty')}</EmptyHint>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t(RANGE_LABEL_KEY[range])}
      </Typography>
    </CardShell>
  );
}

function ScoreRing({ value }: { value: number | null }) {
  const v = value !== null ? Math.max(0, Math.min(100, Math.round(value))) : 0;
  const deg = (v / 100) * 360;
  const color = value !== null ? COLORS.geo : '#E6E7EB';
  return (
    <Box
      sx={{
        position: 'relative',
        width: 116,
        height: 116,
        borderRadius: '50%',
        flexShrink: 0,
        background: `conic-gradient(${color} 0deg ${deg}deg, #EEEEF1 ${deg}deg 360deg)`,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 11, borderRadius: '50%', bgcolor: 'background.paper' }} />
      <Box sx={{ position: 'relative', textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1, letterSpacing: '-0.02em' }}>
          {value !== null ? Math.round(value) : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          / 100
        </Typography>
      </Box>
    </Box>
  );
}

function AxisBar({ label, value, color }: { label: string; value: number | null; color: string }) {
  const pct = value !== null ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color, minWidth: 72 }}>{label}</Typography>
      <Box sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: 'rgba(11,11,15,0.06)', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: 14, width: 24, textAlign: 'right' }}>
        {value !== null ? Math.round(value) : '—'}
      </Typography>
    </Box>
  );
}

/* ──────────────── Tasks ──────────────── */

const PERSONA_META: Record<string, { labelKey: string; color: string; bg: string }> = {
  technique: { labelKey: 'dashboard.persona.technique', color: '#7C3AED', bg: 'rgba(124,58,237,0.10)' },
  contenu: { labelKey: 'dashboard.persona.contenu', color: '#0E9F6E', bg: 'rgba(14,159,110,0.10)' },
  hors_site: { labelKey: 'dashboard.persona.horsSite', color: '#EA580C', bg: 'rgba(234,88,12,0.10)' },
};
const PRIORITY_META: Record<string, { labelKey: string; color: string }> = {
  critical: { labelKey: 'dashboard.priority.critical', color: '#EF4444' },
  high: { labelKey: 'dashboard.priority.high', color: '#F97316' },
  medium: { labelKey: 'dashboard.priority.medium', color: '#F59E0B' },
  low: { labelKey: 'dashboard.priority.low', color: '#5F6470' },
};

function TasksCard({ tasks, isFirstAudit }: { tasks: Task[]; isFirstAudit: boolean }) {
  const { t } = useTranslation();
  const top = tasks.slice(0, 3);
  const remaining = tasks.length - top.length;
  return (
    <CardShell title={t('dashboard.tasks.title')} link={{ label: t('dashboard.tasks.viewAll'), to: '/tasks' }}>
      {top.length === 0 ? (
        <EmptyHint>{t('dashboard.tasks.empty')}</EmptyHint>
      ) : (
        <Stack spacing={1}>
          {top.map((task) => (
            <TaskRow key={task.taskId} task={task} />
          ))}
          {tasks.length > top.length && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
              {isFirstAudit
                ? t('dashboard.tasks.moreFromFirstAudit', { count: remaining })
                : t('dashboard.tasks.moreOthers', { count: remaining })}
            </Typography>
          )}
        </Stack>
      )}
    </CardShell>
  );
}

function TaskRow({ task }: { task: Task }) {
  const { t } = useTranslation();
  const personaMeta = PERSONA_META[task.personaTab];
  const persona = personaMeta
    ? { label: t(personaMeta.labelKey), color: personaMeta.color, bg: personaMeta.bg }
    : { label: task.personaTab, color: '#5F6470', bg: 'rgba(11,11,15,0.06)' };
  const priorityMeta = PRIORITY_META[task.priority];
  const priority = priorityMeta
    ? { label: t(priorityMeta.labelKey), color: priorityMeta.color }
    : { label: task.priority, color: '#5F6470' };
  return (
    <Box
      component={RouterLink}
      to={`/tasks?openTask=${task.taskId}`}
      sx={{
        display: 'block',
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(124,58,237,0.04)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', fontSize: 13, lineHeight: 1.35 }}>
          {task.title}
        </Typography>
        <Box sx={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, px: 0.75, py: 0.25, borderRadius: 0.75, color: persona.color, bgcolor: persona.bg, lineHeight: 1.4 }}>
          {persona.label}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: priority.color }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {priority.label}
          {task.status === 'in_progress' && ` · ${t('dashboard.tasks.inProgress')}`}
        </Typography>
      </Box>
    </Box>
  );
}

/* ──────────────── Concurrents ──────────────── */

function CompetitorsCard({
  matrix,
  brandName,
  brandId,
  competitorDomains,
  isFirstAudit,
  onCta,
}: {
  matrix: AxisMatrixResponse | null;
  brandName: string;
  brandId: string;
  competitorDomains: Record<string, string>;
  isFirstAudit: boolean;
  onCta: () => void;
}) {
  const { t } = useTranslation();
  const rows = useMemo(() => competitorRanking(matrix, brandName), [matrix, brandName]);
  const top = rows.slice(0, 5);
  // The CTA targets one place ABOVE the brand's current rank (rows are sorted by
  // % vues desc, so rank = index + 1). At #1 there is nothing to climb to, so the
  // CTA is dropped entirely rather than inviting the user to "reach #1" again.
  const brandRank = rows.findIndex((r) => r.isBrand) + 1;
  const ctaTarget = brandRank > 1 ? brandRank - 1 : null;
  return (
    <CardShell title={t('dashboard.competitors.title')} dotColor={COLORS.referral} link={{ label: t('dashboard.competitors.link'), to: '/competitors' }}>
      {top.length === 0 ? (
        <EmptyHint>
          {isFirstAudit
            ? t('dashboard.competitors.emptyFirstAudit')
            : t('dashboard.competitors.empty')}
        </EmptyHint>
      ) : (
        <>
          <Stack spacing={0.25}>
            {top.map((r, i) => (
              <CompetitorRow
                key={r.name}
                row={r}
                pos={i + 1}
                brandId={brandId}
                domain={competitorDomains[foldCompetitorName(r.name)]}
              />
            ))}
          </Stack>
          {ctaTarget !== null && (
            <CtaLine label={t('dashboard.competitors.cta', { position: ctaTarget })} color={COLORS.referral} onClick={onCta} />
          )}
        </>
      )}
    </CardShell>
  );
}

function CompetitorRow({ row, pos, brandId, domain }: { row: CompetitorRankRow; pos: number; brandId: string; domain?: string }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1,
        borderRadius: 1,
        ...(row.isBrand ? { bgcolor: 'rgba(124,58,237,0.07)', boxShadow: 'inset 0 0 0 1px rgba(124,58,237,0.16)' } : {}),
      }}
    >
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0, bgcolor: row.isBrand ? 'primary.main' : 'rgba(11,11,15,0.06)', color: row.isBrand ? '#fff' : 'text.secondary' }}>
        {pos}
      </Box>
      {row.isBrand ? (
        <BrandFavicon brandId={brandId} size={22} />
      ) : (
        // Competitor favicon by DOMAIN (cross-tenant cache), same as the
        // Competitors page. Falls back to a colored initial when the name has
        // no tracked domain.
        <DomainFavicon domain={domain} name={row.name} size={22} />
      )}
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: 13.5 }} noWrap>
        {row.name}
        {row.isBrand && (
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
            {t('dashboard.competitors.yourBrand')}
          </Box>
        )}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>
        {row.pct !== null ? `${row.pct}%` : '—'}{' '}
        <Box component="span" sx={{ fontSize: 10.5, fontWeight: 500, color: 'text.secondary' }}>
          {t('dashboard.competitors.views')}
        </Box>
      </Typography>
    </Box>
  );
}

/* ──────────────── LLM qui vous citent ──────────────── */

function EnginesCard({
  narrative,
  authority,
  referral,
  isFirstAudit,
}: {
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
  isFirstAudit: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const counts = mergeEngineCitationCounts(
    latestEngineCitationCounts(narrative, (a) => a.brandNarrativeSummary),
    latestEngineCitationCounts(authority, (a) => a.brandAuthoritySummary),
    latestEngineCitationCounts(referral, (a) => a.brandReferralSummary),
  );
  // Tiebreak order only. Secondary engines last — an id absent here scores
  // indexOf === -1 and would sort ahead of every primary on an equal count.
  const order = ['claude', 'openai', 'chatgpt', 'gemini', 'perplexity', 'mistral', 'ai_overview'];
  const engines = Object.keys(counts).sort((a, b) => {
    const byCount = (counts[b] ?? 0) - (counts[a] ?? 0);
    if (byCount !== 0) return byCount;
    return order.indexOf(a) - order.indexOf(b);
  });
  const max = Math.max(1, ...engines.map((e) => counts[e] ?? 0));

  return (
    <CardShell title={t('dashboard.engines.title')} dotColor={COLORS.geo} link={{ label: t('dashboard.detail'), to: '/brand/authority' }}>
      {engines.length === 0 ? (
        <EmptyHint>
          {isFirstAudit
            ? t('dashboard.engines.emptyFirstAudit')
            : t('dashboard.engines.empty')}
        </EmptyHint>
      ) : (
        <>
          <Stack spacing={1.25}>
            {engines.map((e) => {
              const n = counts[e] ?? 0;
              return (
                <Box key={e} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1, display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: '#fff', boxShadow: 'inset 0 0 0 1px rgba(11,11,15,0.08)' }}>
                    <EngineLogo engine={e} size={18} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13.5 }}>{ENGINE_LABEL[e] || e}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 14, color: n === 0 ? 'text.secondary' : 'text.primary' }}>
                        {n} <Box component="span" sx={{ fontSize: 11.5, fontWeight: 500, color: 'text.secondary' }}>{t('dashboard.engines.citations', { count: n })}</Box>
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(11,11,15,0.06)', overflow: 'hidden', mt: 0.5 }}>
                      <Box sx={{ width: `${Math.max(3, (n / max) * 100)}%`, height: '100%', bgcolor: ENGINE_COLOR[e] || '#888' }} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('dashboard.engines.caption')}
          </Typography>
          <CtaLine label={t('dashboard.engines.cta')} color={COLORS.geo} onClick={() => navigate('/brand/authority')} />
        </>
      )}
    </CardShell>
  );
}

/* ──────────────── Pages citées par les IA ──────────────── */

function CitedPagesCard({ narrative, authority, referral }: { narrative: Audit[]; authority: Audit[]; referral: Audit[] }) {
  const { t } = useTranslation();
  const [view, setView] = useState<'top' | 'bottom'>('top');
  const [expanded, setExpanded] = useState(false);

  const pages = useMemo<CitedPageEntry[]>(() => {
    const fromAuthority = latestCitedPages(authority, (a) => a.brandAuthoritySummary);
    if (fromAuthority.length > 0) return fromAuthority;
    const fromReferral = latestCitedPages(referral, (a) => a.brandReferralSummary);
    if (fromReferral.length > 0) return fromReferral;
    return latestCitedPages(narrative, (a) => a.brandNarrativeSummary);
  }, [narrative, authority, referral]);

  const sorted = useMemo(() => {
    const arr = [...pages];
    arr.sort((a, b) => (view === 'top' ? b.count - a.count : a.count - b.count));
    return arr;
  }, [pages, view]);

  const visible = expanded ? sorted : sorted.slice(0, 3);

  return (
    <CardShell
      title={t('dashboard.pages.title')}
      dotColor={COLORS.authority}
      action={
        <ToggleButtonGroup value={view} exclusive size="small" onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="top" sx={{ py: 0.25, px: 1, fontSize: 11 }}>{t('dashboard.pages.topCited')}</ToggleButton>
          <ToggleButton value="bottom" sx={{ py: 0.25, px: 1, fontSize: 11 }}>{t('dashboard.pages.leastCited')}</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {sorted.length === 0 ? (
        <EmptyHint>{t('dashboard.pages.empty')}</EmptyHint>
      ) : (
        <>
          <Stack>
            {visible.map((p, i) => (
              <CitedPageRow key={p.url} page={p} rank={view === 'top' ? i + 1 : sorted.length - i} low={view === 'bottom'} />
            ))}
          </Stack>
          {sorted.length > 3 && (
            <Button fullWidth size="small" onClick={() => setExpanded((v) => !v)} sx={{ mt: 1 }}>
              {expanded ? t('dashboard.pages.collapse') : t('dashboard.pages.showMore', { count: sorted.length - 3 })}
            </Button>
          )}
        </>
      )}
    </CardShell>
  );
}

function pagePath(url: string): string {
  try {
    const u = new URL(url.includes('://') ? url : `https://${url}`);
    return (u.pathname === '/' ? u.hostname : `${u.hostname}${u.pathname}`).replace(/\/$/, '');
  } catch {
    return url;
  }
}

function CitedPageRow({ page, rank, low }: { page: CitedPageEntry; rank: number; low: boolean }) {
  const path = pagePath(page.url);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: low ? 'text.secondary' : 'primary.main', width: 18, flexShrink: 0 }}>{rank}</Typography>
      <Tooltip title={page.url}>
        <Typography variant="body2" sx={{ flex: 1, fontSize: 13, fontWeight: 500 }} noWrap>
          {path}
        </Typography>
      </Tooltip>
      <Box sx={{ flexShrink: 0, fontSize: 12, fontWeight: 800, px: 1, py: 0.25, borderRadius: 999, color: '#fff', bgcolor: low ? COLORS.down : COLORS.up }}>
        {page.count}×
      </Box>
    </Box>
  );
}

/* ──────────────── Classement par zone ──────────────── */

function ZonesCard({
  referral,
  locations,
  isFirstAudit,
}: {
  referral: Audit[];
  locations?: { zone: string; countryCode?: string; scale?: string }[];
  isFirstAudit: boolean;
}) {
  const { t } = useTranslation();
  // zone label → persisted {countryCode, scale} from the brand's Locations,
  // when present. The dict resolves everything else instantly.
  const storedByZone = useMemo(() => {
    const m = new Map<string, { countryCode?: string; scale?: string }>();
    for (const l of locations || []) {
      if (l.zone) m.set(l.zone.toLowerCase(), { countryCode: l.countryCode, scale: l.scale });
    }
    return m;
  }, [locations]);

  const zones = useMemo(() => {
    const withZones = referral.filter((a) => a.brandReferralSummary?.zones && Object.keys(a.brandReferralSummary.zones).length > 0);
    const latest = withZones[withZones.length - 1]?.brandReferralSummary?.zones;
    const prev = withZones[withZones.length - 2]?.brandReferralSummary?.zones;
    if (!latest) return [];
    return Object.entries(latest)
      .map(([zone, b]: [string, ZoneVisibilityBreakdown]) => {
        const resolved = resolveZone(zone, storedByZone.get(zone.toLowerCase()));
        return {
          zone,
          score: Math.round(b.score),
          delta: prev && prev[zone] ? Math.round(b.score - prev[zone].score) : null,
          resolved,
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [referral, storedByZone]);

  return (
    <CardShell title={t('dashboard.zones.title')} dotColor={COLORS.referral} link={{ label: t('dashboard.detail'), to: '/brand/referral' }}>
      {zones.length === 0 ? (
        <EmptyHint>{t('dashboard.zones.empty')}</EmptyHint>
      ) : (
        <Stack spacing={0.25}>
          {zones.map((z, i) => (
            <RankBarRow
              key={z.zone}
              label={z.zone}
              score={z.score}
              delta={z.delta}
              isFirst={isFirstAudit}
              best={i === 0 ? t('dashboard.zones.best') : undefined}
              meta={sizeLabel(z.resolved.size)}
              flag={<ZoneFlag zone={z.resolved} size={24} title={z.zone} />}
            />
          ))}
        </Stack>
      )}
    </CardShell>
  );
}

/* ──────────────── Classement par langue ──────────────── */

function LanguagesCard({
  narrative,
  authority,
  referral,
  isFirstAudit,
}: {
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
  isFirstAudit: boolean;
}) {
  const { t } = useTranslation();
  const langs = useMemo<{ language: string; score: number; promptCount: number }[]>(() => {
    const collect = (arr: Audit[], pick: (a: Audit) => { language_scores?: LanguageScoreEntry[] } | undefined) =>
      latestLanguageScores(arr, pick);
    const all = [
      ...collect(narrative, (a) => a.brandNarrativeSummary),
      ...collect(authority, (a) => a.brandAuthoritySummary),
      ...collect(referral, (a) => a.brandReferralSummary),
    ];
    const agg = new Map<string, { sum: number; count: number }>();
    for (const e of all) {
      const cur = agg.get(e.language) || { sum: 0, count: 0 };
      cur.sum += e.score * e.promptCount;
      cur.count += e.promptCount;
      agg.set(e.language, cur);
    }
    return [...agg.entries()]
      .map(([language, { sum, count }]) => ({ language, score: count ? Math.round(sum / count) : 0, promptCount: count }))
      .sort((a, b) => b.score - a.score);
  }, [narrative, authority, referral]);

  return (
    <CardShell title={t('dashboard.languages.title')} dotColor={COLORS.earnedMedia} link={{ label: t('dashboard.detail'), to: '/brand/referral' }}>
      {langs.length === 0 ? (
        <EmptyHint>{t('dashboard.languages.empty')}</EmptyHint>
      ) : (
        <Stack spacing={0.25}>
          {langs.map((l, i) => {
            const langLabel = LANG_LABEL_KEY[l.language] ? t(LANG_LABEL_KEY[l.language]) : l.language.toUpperCase();
            return (
            <RankBarRow
              key={l.language}
              label={langLabel}
              score={l.score}
              delta={null}
              isFirst={isFirstAudit}
              best={i === 0 ? t('dashboard.languages.strong') : undefined}
              meta={t('dashboard.languages.promptCount', { count: l.promptCount })}
              flag={<Flag countryCode={languageFlag(l.language)} size={24} title={langLabel} />}
            />
            );
          })}
        </Stack>
      )}
    </CardShell>
  );
}

/* ──────────────── shared ranking row + CTA ──────────────── */

function RankBarRow({
  label,
  score,
  delta,
  isFirst,
  best,
  meta,
  flag,
}: {
  label: string;
  score: number;
  delta: number | null;
  isFirst: boolean;
  best?: string;
  meta?: string;
  flag?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1, '&:hover': { bgcolor: 'rgba(11,11,15,0.03)' } }}>
      {flag && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{flag}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 14 }} noWrap>{label}</Typography>
          {best && (
            <Box sx={{ fontSize: 9.5, fontWeight: 800, px: 0.75, py: 0.125, borderRadius: 999, bgcolor: COLORS.up, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {best}
            </Box>
          )}
        </Box>
        {meta && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5 }}>{meta}</Typography>}
      </Box>
      <Box sx={{ width: 90, height: 7, borderRadius: 4, bgcolor: 'rgba(11,11,15,0.06)', overflow: 'hidden', flexShrink: 0 }}>
        <Box sx={{ width: `${Math.max(0, Math.min(100, score))}%`, height: '100%', bgcolor: scoreBarColor(score) }} />
      </Box>
      <Typography sx={{ width: 38, textAlign: 'right', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
        {score}
        <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>/100</Box>
      </Typography>
      <Box sx={{ width: 40, textAlign: 'right', fontSize: 12, fontWeight: 800, flexShrink: 0, color: delta === null ? 'text.secondary' : delta > 0 ? COLORS.up : delta < 0 ? COLORS.down : 'text.secondary' }}>
        {isFirst ? '1ᵉʳ' : delta === null ? '—' : delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : '—'}
      </Box>
    </Box>
  );
}

function CtaLine({ label, color, onClick }: { label: string; color: string; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        mt: 1.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.25,
        borderRadius: 1.5,
        cursor: 'pointer',
        border: '1px dashed',
        borderColor: `${color}66`,
        bgcolor: `${color}0F`,
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: `${color}1A` },
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color }}>{label}</Typography>
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#fff', bgcolor: color, fontSize: 17, fontWeight: 700, lineHeight: 0, flexShrink: 0 }}>
        +
      </Box>
    </Box>
  );
}

/* ──────────────── Customize drawer ──────────────── */

function CustomizeDrawer({
  open,
  onClose,
  layout,
}: {
  open: boolean;
  onClose: () => void;
  layout: ReturnType<typeof useDashboardLayout>;
}) {
  const { t } = useTranslation();
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 340, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: 16 }}>
            {t('dashboard.customizeDrawer.title')}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          {CARD_DEFS.map((c) => (
            <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(11,11,15,0.03)' } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13.5 }}>{t(c.labelKey)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5 }}>{t(c.subKey)}</Typography>
              </Box>
              <Switch checked={layout.isVisible(c.id)} onChange={() => layout.toggle(c.id)} />
            </Box>
          ))}
        </Box>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
          {t('dashboard.customizeDrawer.footer')}
        </Typography>
      </Box>
    </Drawer>
  );
}
